import React from 'react';
import { motion } from 'framer-motion';
import { IconWrapper } from '../../components/common/IconWrapper';
import {
  HiStar,
  HiUsers,
  HiBuildingOffice,
  HiMiniSparkles,
  HiCheckCircle,
  HiGlobeAlt,
  HiDevicePhoneMobile,
  HiComputerDesktop
} from 'react-icons/hi2';

const heroImage = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=60';

const keyMetrics = [
  { metric: 'Platforms delivered', value: '3' },
  { metric: 'Courses managed', value: '50+' },
  { metric: 'Active users', value: '5K+' }
];

const results = [
  { metric: 'User Engagement', value: '+120%' },
  { metric: 'Course Completion', value: '+85%' },
  { metric: 'Operational Efficiency', value: '+90%' },
  { metric: 'Mobile App Downloads', value: '10K+' }
];

const painPoints = [
  'Fragmented systems for online and offline course delivery causing inconsistent user experience',
  'No unified platform to manage students across multiple offline centers and online courses',
  'Manual processes for course management, enrollment, and progress tracking',
  'Lack of mobile access preventing students from learning on-the-go',
  'Inefficient internal operations requiring manual coordination between employees'
];

const strategy = [
  'Built responsive web application using React.js for dynamic course delivery and enrollment management',
  'Developed native mobile apps for iOS and Android using React Native for seamless cross-platform experience',
  'Implemented Node.js backend with MySQL database for scalable data management and API services',
  'Created internal management portal for employees to manage courses, students, analytics, and operations',
  'Integrated offline access capabilities in mobile apps for course materials and progress tracking'
];

const outcomes = [
  { title: 'Unified Learning Experience', detail: 'Students can seamlessly access courses across web and mobile platforms, with synchronized progress tracking and offline capabilities' },
  { title: 'Operational Excellence', detail: 'Internal management portal streamlined administrative tasks, reducing manual workload by 90% and improving coordination between employees' },
  { title: 'Scalable Growth', detail: 'Platform architecture supports growing number of courses, students, and offline centers with robust performance and data management' }
];

const timeline = [
  { phase: 'Planning & Design', duration: '3 weeks', focus: 'Requirements gathering, architecture design, and UI/UX planning' },
  { phase: 'Web Development', duration: '6 weeks', focus: 'React frontend, Node.js backend, and MySQL database implementation' },
  { phase: 'Mobile Development', duration: '8 weeks', focus: 'iOS and Android app development with React Native' },
  { phase: 'Management Portal', duration: '4 weeks', focus: 'Internal portal development and employee management features' },
  { phase: 'Testing & Launch', duration: '3 weeks', focus: 'Quality assurance, deployment, and platform launch' }
];

const technologies = ['React', 'Node.js', 'MySQL', 'React Native', 'iOS', 'Android', 'Express.js', 'JavaScript'];

const services = ['Application Engineering', 'Mobile Development', 'Web Development', 'Database Design'];

const testimonial = {
  quote: 'FirstLogic delivered a comprehensive solution that transformed how we deliver courses. The web platform and mobile apps have significantly improved student engagement, and the internal portal has streamlined our operations.',
  author: 'OSOP Coding Team',
  role: 'OSOP Coding Management'
};

const OSOPCodingPlatformPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Web Application & Mobile Apps Development"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 dark:bg-slate-900/40 backdrop-blur-sm border border-white/40 dark:border-slate-700 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <IconWrapper icon={HiStar} className="w-5 h-5 text-blue-400 mr-2" />
                <span className="text-sm font-medium text-white">
                  Education Technology Case Study
                </span>
              </motion.div>

              <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Web Application & Mobile Apps Development
              </motion.h1>
              <motion.p
                className="text-lg text-white/75 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                OSOP Coding - Technology & Programming Courses Platform
              </motion.p>

              <motion.p
                className="text-xl text-white/85 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Comprehensive web application and cross-platform mobile apps (iOS & Android) for technology and programming courses, with internal management portal for employees.
              </motion.p>

              <motion.div
                className="flex flex-wrap items-center gap-6 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <div className="flex items-center">
                  <IconWrapper icon={HiUsers} className="w-5 h-5 text-blue-400 mr-2" />
                  <span className="text-white/90 font-medium">
                    OSOP Coding
                  </span>
                </div>
                <div className="flex items-center">
                  <IconWrapper icon={HiBuildingOffice} className="w-5 h-5 text-blue-400 mr-2" />
                  <span className="text-white/90 font-medium">
                    Education
                  </span>
                </div>
                <div className="flex items-center">
                  <IconWrapper icon={HiGlobeAlt} className="w-5 h-5 text-blue-400 mr-2" />
                  <a 
                    href="https://www.osopcoding.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/90 font-medium hover:text-white underline"
                  >
                    osopcoding.com
                  </a>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="relative w-full h-80 lg:h-[440px] rounded-3xl overflow-hidden shadow-2xl border border-white/30 bg-white/10 backdrop-blur-sm">
                <img
                  src={heroImage}
                  alt="Web Application & Mobile Apps Development"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 to-slate-900/40" />
                <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-3">
                  {services.map(service => (
                    <span
                      key={service}
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 dark:bg-slate-900/90 text-slate-800 dark:text-white backdrop-blur-sm"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {keyMetrics.map((metric, index) => (
              <motion.div
                key={metric.metric}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm"
              >
                <div className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                  {metric.metric}
                </div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">
                  {metric.value}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Key Results</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {results.map((result, index) => (
              <motion.div
                key={result.metric}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm text-center"
              >
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                  {result.value}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  {result.metric}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="p-8 rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Pain Points</h3>
            <ul className="space-y-3">
              {painPoints.map(point => (
                <li key={point} className="flex items-start text-slate-600 dark:text-slate-300">
                  <span className="mt-1 mr-3">
                    <IconWrapper icon={HiMiniSparkles} className="w-5 h-5 text-blue-500" />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Strategy</h3>
            <ol className="space-y-4 list-decimal list-inside text-slate-600 dark:text-slate-300">
              {strategy.map(step => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 text-center">
            Results & Impact
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {outcomes.map(outcome => (
              <motion.div
                key={outcome.title}
                className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-2">
                  {outcome.title}
                </div>
                <p className="text-slate-600 dark:text-slate-300">{outcome.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8">
            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-8 bg-white dark:bg-slate-950 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Engagement Timeline</h3>
              <div className="space-y-6">
                {timeline.map(item => (
                  <div key={item.phase} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200 font-semibold flex items-center justify-center">
                      {item.phase.split(' ')[0]}
                    </div>
                    <div>
                      <div className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {item.duration}
                      </div>
                      <div className="text-lg font-semibold text-slate-900 dark:text-white">
                        {item.phase}
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 text-sm">{item.focus}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-8 bg-white dark:bg-slate-950 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Technologies</h3>
              <div className="flex flex-wrap gap-3 mb-6">
                {technologies.map(tech => (
                  <span
                    key={tech}
                    className="px-3 py-1.5 rounded-full text-sm bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white"
                  >
                    {tech}
                  </span>
                ))}
              </div>
              
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-2">
                    <IconWrapper icon={HiComputerDesktop} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <span className="font-semibold text-slate-900 dark:text-white">Web Platform</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    React + Node.js + MySQL
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 mb-2">
                    <IconWrapper icon={HiDevicePhoneMobile} className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <span className="font-semibold text-slate-900 dark:text-white">Mobile Apps</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    iOS & Android (React Native)
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 mb-2">
                    <IconWrapper icon={HiGlobeAlt} className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    <span className="font-semibold text-slate-900 dark:text-white">Website</span>
                  </div>
                  <a 
                    href="https://www.osopcoding.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 underline"
                  >
                    www.osopcoding.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center p-10 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-3xl text-slate-900 dark:text-white shadow-sm"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <IconWrapper icon={HiStar} className="w-12 h-12 text-blue-500 mx-auto mb-6" />
            <blockquote className="text-2xl italic mb-8 leading-relaxed text-slate-700 dark:text-slate-200">
              "{testimonial.quote}"
            </blockquote>
            <div>
              <div className="font-semibold text-lg">{testimonial.author}</div>
              <div className="text-slate-500 dark:text-slate-400">{testimonial.role}</div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="relative p-10 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden bg-white dark:bg-slate-950"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-slate-100 dark:from-slate-900 dark:to-slate-950" />
            <div className="relative z-10">
              <motion.h2
                className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Ready to Start Your Engagement?
              </motion.h2>
              <motion.p
                className="text-lg text-slate-600 dark:text-slate-300 mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Let's discuss how we can deliver the same outcomes for your teams.
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <motion.button
                  className="px-8 py-4 bg-blue-600 text-white rounded-full font-semibold text-lg hover:bg-blue-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = '/contact')}
                >
                  Start Your Project
                </motion.button>
                <motion.button
                  className="px-8 py-4 bg-transparent border-2 border-blue-600 dark:border-slate-700 text-blue-600 dark:text-white rounded-full font-semibold text-lg hover:bg-blue-50 dark:hover:bg-slate-900 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = '/contact')}
                >
                  Schedule Consultation
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OSOPCodingPlatformPage;

